/**
 * Subject-side (master column) pivot fields of the kohandustabel.
 *
 * These five fields are the evaluator-editable prediction inputs: the backend
 * one-hot-encodes them into the ML feature vector (PivotFieldRegistry +
 * ValuationFeatureBuilder), reading each from a specific home on the
 * evaluation row. The `save` key here MUST match the home the backend
 * extractor reads (ValuationFeatureBuilder::buildForEvaluation match arms),
 * or the edited value never reaches the model — see RED-262.
 *
 * `detail` names the detail-store section mirroring the same value in the UI.
 */
export const MASTER_PIVOT_FIELDS = {
  ehitise_seisukord: { save: 'construction', detail: 'ehrConstruction' },
  hooneosa_seisukord: { save: 'interior', detail: 'interior' },
  kinnistu_parkimine: { save: 'landboard', detail: 'landBoard' },
  rodu: { save: 'construction', detail: 'ehrConstruction' },
  panipaik: { save: 'construction', detail: 'ehrConstruction' },
}

/**
 * The five pivot field keys, shared by BOTH the subject (master) branch and the
 * comparable branch of `updateComparisonTransactionField`. Editing any of these
 * on a comparable must re-drive the AI adjustments exactly like the subject does
 * (RED-262). Keep this the single source of truth so the two branches can't drift.
 */
export const PIVOT_FIELD_KEYS = Object.keys(MASTER_PIVOT_FIELDS)

/**
 * Whether a master-column edit changes a prediction input and therefore
 * must trigger an AI adjustment recompute (mirroring the comparable-side
 * pivot-edit path).
 */
export const isMasterPivotField = fieldKey =>
  Object.hasOwn(MASTER_PIVOT_FIELDS, fieldKey)

/**
 * The evaluation.save payload persisting a master pivot edit into the home
 * the backend feature builder reads it from.
 */
export const masterPivotSavePayload = (fieldKey, value) => {
  const pivot = MASTER_PIVOT_FIELDS[fieldKey]
  if (!pivot) return null
  return { [pivot.save]: { [fieldKey]: value } }
}

/**
 * RED-267: subject-side land element fields of the land kohandustabel.
 * Deliberately a SIBLING of MASTER_PIVOT_FIELDS, never merged into it —
 * PIVOT_FIELD_KEYS also drives the comparable-side ML pivot POST + AI
 * recompute (RED-262), and land fields are not prediction inputs.
 *
 * `save` = the EvaluationController::save JSON bucket; `store` picks the
 * frontend home ('detail' → detailStore[detail], 'infra' → useInfraStore);
 * `field` = the key inside that home (table key ≠ detail key for two).
 */
export const MASTER_LAND_FIELDS = {
  juurdepaas: { save: 'infra', store: 'infra', detail: 'infra', field: 'juurdepaas' },
  ehitusoigus: { save: 'landboard', store: 'detail', detail: 'landBoard', field: 'detailplaneering' },
  haljastus: { save: 'landboard', store: 'detail', detail: 'landBoard', field: 'kinnistu_haljastus' },
}

export const isMasterLandField = fieldKey =>
  Object.hasOwn(MASTER_LAND_FIELDS, fieldKey)

/**
 * The evaluation.save payload persisting a land master edit into the home
 * the Detail-view card reads it from.
 */
export const masterLandSavePayload = (fieldKey, value) => {
  const landField = MASTER_LAND_FIELDS[fieldKey]
  if (!landField) return null
  return { [landField.save]: { [landField.field]: value } }
}
